import { parseJsonObject } from "@/lib/provisioning";
import type { ResourceStatus, RetryProvisioningResult } from "@/types/admin";

/** Order statuses under which an Error VM is still owed a machine (the backend's own list). */
const ORDER_STATUSES_OWING_A_FAILED_VM = ["Paid", "Processing", "Complete", "Failed"];

/**
 * Superseded (2026-09-28) by `useProvisioningRetryCheck`'s batch call to
 * `POST /api/v1/admin/resources/retry-provisioning/check` — the menu item in `ResourceDetailPage`
 * and the row action in `ResourceActionsDropdown` read that answer directly and never call this.
 * Kept only as a reference for what the server checks; not called anywhere.
 *
 * Whether to offer "Retry provisioning" for a resource. Mirrors the checks the backend can answer
 * from this page's data — a VM that never ran, on a new-service order, and an Error left by the
 * build (`requiresManualVerification`) on an order still owing it. The backend checks the rest
 * (billing cycle, a create that may be in flight) and refuses with the reason.
 */
export function canRetryProvisioning(resource: {
  type: string;
  status: ResourceStatus;
  provisionedAt?: string | null;
  orderStatus?: string | null;
  orderType?: string | null;
  metadata?: string | null;
}): boolean {
  if (resource.type !== "VirtualMachine") {
    return false;
  }

  if (resource.provisionedAt) {
    return false;
  }

  const orderStatus = resource.orderStatus ?? null;
  if (orderStatus === null) {
    return false;
  }

  if (resource.orderType && resource.orderType !== "NewService") {
    return false;
  }

  // The server now only accepts a retry on Error (Pending/Provisioning return 409
  // RESOURCE_STATUS_NOT_RETRYABLE). Uncomment to restore the old Pending/Provisioning offers.
  // if (resource.status === "Pending") {
  //   return orderStatus === "Paid";
  // }
  //
  // if (resource.status === "Provisioning") {
  //   return true;
  // }

  if (resource.status === "Error") {
    const leftByTheBuild = parseJsonObject(resource.metadata).requiresManualVerification === true;
    return leftByTheBuild && ORDER_STATUSES_OWING_A_FAILED_VM.includes(orderStatus);
  }

  return false;
}

/** What confirming the retry will do, per status, for the confirmation dialog. */
export function describeRetryProvisioning(status: ResourceStatus): string {
  const expiryNote =
    " The billing period is not extended: if the customer lost days, correct the expiry afterwards.";

  // The server now only accepts a retry on Error (Pending/Provisioning return 409
  // RESOURCE_STATUS_NOT_RETRYABLE). Uncomment to restore the old Pending/Provisioning copy.
  // if (status === "Pending") {
  //   return (
  //     "Runs the paid order's fulfilment again: opens the billing cycle and starts building every VM on the order." +
  //     expiryNote
  //   );
  // }

  if (status === "Error") {
    return (
      "Moves the VM and its failed disks and networks back to Provisioning, reopens a failed order (back to Processing), clears the error shown to the customer and queues the build again. " +
      "A VM that already has a provider id is only re-checked on the provider; one without asks the provider to create it." +
      expiryNote
    );
  }

  // if (status === "Provisioning") {
  //   return (
  //     "Queues the provisioning job again. A VM that already has a provider id is only re-checked on the provider; one without asks the provider to create it." +
  //     expiryNote
  //   );
  // }

  // The item is now offered only when the server flags the resource retryable, which only happens
  // in Error — this branch is reached only if the server's flag and status disagree by the time of submit.
  return `A VM in ${status} cannot be retried: only Error can. The server will refuse and say why.`;
}

/** The success toast's description for what the retry started. */
export function describeRetryOutcome(result: RetryProvisioningResult): string {
  if (result.outcome === "OrderFulfilmentRestarted") {
    return "The order is being fulfilled; the VM moves to Provisioning shortly.";
  }

  let action = result.resumesExistingVm
    ? "Re-checking the existing machine on the provider"
    : "Asking the provider to create the machine";
  if (result.droppedVmUuid) {
    action = `The infrastructure no longer has machine ${result.droppedVmUuid}; its id was dropped and the provider is asked to create a new one`;
  }

  const extras: string[] = [];
  if (result.childrenReset > 0) {
    extras.push(`${result.childrenReset} disk/network reset`);
  }
  if (result.orderReopened) {
    extras.push("order reopened");
  }

  if (extras.length === 0) {
    return `${action}.`;
  }

  return `${action} (${extras.join(", ")}).`;
}

/**
 * Superseded (2026-09-28) by the server-computed `machineMayExist` on the retry check, which is true
 * for every VM with no uuid — `RetryProvisioningDialog` reads that and never calls this. Kept only as
 * a reference; not called anywhere.
 *
 * Whether a retry could build a second machine: a VM in Error with no provider id whose create
 * request got no clear answer. WiServiceClient treats every create failure as possibly carried out
 * — a timeout, a dropped connection, a 5xx, an unreadable reply — except a provider that answered
 * and refused (HTTP 4xx). With no id recorded, a retry sends a fresh create, so if the machine exists
 * the customer gets two. A refusal, or a job failure before the request (not a "VM creation
 * failed" row), reads false.
 */
export function mayAlreadyExistOnProvider(resource: {
  status: ResourceStatus;
  providerResourceId?: string | null;
  metadata?: string | null;
  vm?: { uuid?: string | null } | null;
}): boolean {
  if (resource.status !== "Error") {
    return false;
  }

  const metadata = parseJsonObject(resource.metadata);
  const recordedUuid = [resource.providerResourceId, resource.vm?.uuid, metadata.VmUuid].some(
    (value) => typeof value === "string" && value.trim() !== "",
  );
  if (recordedUuid) {
    // The job only re-polls that machine; it never creates another.
    return false;
  }

  // ProvisionResourceJob.HandleCreateVmFailureAsync writes this prefix; lastProviderError holds
  // WiServiceClient's own sentence ("WiService HTTP 400 Bad Request" for a refusal).
  const lastError = typeof metadata.lastError === "string" ? metadata.lastError : "";
  const createCallFailed = lastError.startsWith("VM creation failed:");
  if (!createCallFailed) {
    return false;
  }

  const providerError =
    typeof metadata.lastProviderError === "string" ? metadata.lastProviderError : lastError;
  const providerRefused = /WiService HTTP 4\d\d\b/.test(providerError);

  return !providerRefused;
}
